import React, { useState, useRef } from "react";
import { Info } from "lucide-react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";

/**
 * InfoTip — small contextual help affordance.
 *
 * Why a custom component instead of plain `Tooltip`?
 *   • Radix Tooltip is hover-only and explicitly NOT recommended for touch
 *     devices (it never opens on iPad). The salon use-case absolutely needs
 *     iPad support.
 *   • Radix Popover handles outside-click + Escape + focus management out
 *     of the box, but is click-only by default.
 *
 * This component bridges the two: on devices that support hover (desktop,
 * external trackpad on iPad) it opens on `pointerenter`. On every device
 * it ALSO opens on click, so a tap on iPad / phone works identically.
 *
 * Usage:
 *   <InfoTip title="Lead Machine" text="Form pubblico da inviare via WhatsApp…" />
 *
 * Optional `tip` for an extra line shown below the body in italic gold:
 *   <InfoTip title="Backup" text="…" tip="Esegui un backup prima di importare." />
 */
export function InfoTip({
  title,
  text,
  tip,
  side = "top",
  align = "center",
  size = "sm",          // "sm" → 14px icon, "xs" → 12px (for compact rows)
  iconClassName = "",
  className = "",
  "data-testid": testId,
}) {
  const [open, setOpen] = useState(false);
  // We use a single ref to detect if the device "honors" hover. On purely
  // touch devices `pointerenter` fires synthetically right before `click`,
  // so we suppress hover-open in that case to avoid a flash.
  const lastInteraction = useRef(/** @type {"hover"|"touch"|null} */ (null));

  const iconSizeClass = size === "xs" ? "w-3 h-3" : "w-3.5 h-3.5";

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={title ? `Aiuto: ${title}` : "Aiuto"}
          className={cn(
            "inline-flex items-center justify-center rounded-full text-primary/70 hover:text-primary",
            "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1",
            "align-middle",
            className,
          )}
          onPointerEnter={(e) => {
            if (e.pointerType === "mouse") {
              lastInteraction.current = "hover";
              setOpen(true);
            }
          }}
          onPointerLeave={(e) => {
            if (e.pointerType === "mouse" && lastInteraction.current === "hover") {
              setOpen(false);
            }
          }}
          onPointerDown={(e) => {
            // Remember the pointer type so we can decide what `click` should
            // do. Mouse clicks are redundant (hover already opens) — toggling
            // on click would actually CLOSE a hover-opened tip immediately.
            // Touch / pen taps need explicit toggle behaviour because they
            // never fire pointerenter naturally.
            lastInteraction.current = e.pointerType === "mouse" ? "hover" : "touch";
          }}
          onClick={(e) => {
            // Always block click bubbling so this never triggers a parent
            // (anchor, button) action. The actual open/close logic depends
            // on the device — see comment above.
            e.preventDefault();
            e.stopPropagation();
            if (lastInteraction.current === "touch") {
              setOpen((v) => !v);
            } else {
              // Desktop: click while hover is open is a no-op (keeps it
              // visible). Click without prior hover (e.g. keyboard Enter)
              // opens the tip — Radix' default trigger behaviour.
              setOpen(true);
            }
          }}
          data-testid={testId || "infotip-trigger"}
        >
          <Info className={iconSizeClass} strokeWidth={1.5} aria-hidden="true" />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side={side}
          align={align}
          sideOffset={8}
          collisionPadding={12}
          // Mouse leaving the tip area while hovering → close. We also bind
          // mouseleave on the content so users can move the cursor INTO the
          // tip (e.g. to copy text) without it disappearing.
          onPointerLeave={(e) => {
            if (e.pointerType === "mouse") setOpen(false);
          }}
          // Don't steal focus on hover — only when opened by click/keyboard.
          onOpenAutoFocus={(e) => {
            if (lastInteraction.current === "hover") e.preventDefault();
          }}
          className={cn(
            "z-50 w-64 sm:w-72 rounded-md border border-primary/20 bg-white p-3.5",
            "shadow-[0_8px_30px_rgba(200,169,126,0.18)]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1",
            "data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1",
            "outline-none",
          )}
          data-testid="infotip-content"
        >
          {title && (
            <p className="font-serif-heading text-base text-neutral-900 leading-snug" data-testid="infotip-title">
              {title}
            </p>
          )}
          {text && (
            <p className="text-xs text-neutral-600 mt-1.5 leading-relaxed" data-testid="infotip-text">
              {text}
            </p>
          )}
          {tip && (
            <p className="text-[11px] text-primary mt-2 italic leading-relaxed border-t border-primary/15 pt-2" data-testid="infotip-tip">
              {tip}
            </p>
          )}
          <PopoverPrimitive.Arrow
            width={11}
            height={6}
            className="fill-white drop-shadow-[0_-1px_0_rgba(200,169,126,0.25)]"
          />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export default InfoTip;
